/**
 * Timber sizing — NDS ASD-style simple-span sawn timber beam checks
 * (bending, shear, deflection). Production default uses half-under sections
 * (nominal − 0.5″ each face). See docs/TIMBER_SIZING.md.
 */

export type TimberSpeciesId = 'douglasFir' | 'easternWhitePine' | 'whiteOak';
export type TimberGradeId = 'no2' | 'no1' | 'select';
export type TimberSizeClass = 'beamsAndStringers' | 'postsAndTimbers' | 'dimension';
/** Production default is halfUnder. dressedS4S is for published-table verification. */
export type TimberSizeBasis = 'halfUnder' | 'dressedS4S';
export type TimberMoisture = 'dry' | 'green';
export type DeflectionLimit = 240 | 360;
export type TimberSizingMode = 'spanToSection' | 'sectionToSpan';
export type LoadInputMode = 'psfTrib' | 'plf';
export type GoverningCheck = 'bending' | 'shear' | 'deflection' | 'none';

export interface DesignValueRow {
  Fb: number;
  Ft: number;
  Fv: number;
  FcPerp: number;
  Fc: number;
  E: number;
  Emin: number;
}

/** NDS Supplement 2018 Table 4D — Visually Graded Timbers (5″×5″ and larger). */
export const TIMBER_DESIGN_VALUES: Record<
  TimberSpeciesId,
  Record<'beamsAndStringers' | 'postsAndTimbers', Record<TimberGradeId, DesignValueRow>>
> = {
  douglasFir: {
    beamsAndStringers: {
      select: { Fb: 1600, Ft: 950, Fv: 170, FcPerp: 625, Fc: 1100, E: 1_600_000, Emin: 580_000 },
      no1: { Fb: 1350, Ft: 675, Fv: 170, FcPerp: 625, Fc: 925, E: 1_600_000, Emin: 580_000 },
      no2: { Fb: 875, Ft: 425, Fv: 170, FcPerp: 625, Fc: 600, E: 1_300_000, Emin: 470_000 },
    },
    postsAndTimbers: {
      select: { Fb: 1500, Ft: 1000, Fv: 170, FcPerp: 625, Fc: 1150, E: 1_600_000, Emin: 580_000 },
      no1: { Fb: 1200, Ft: 825, Fv: 170, FcPerp: 625, Fc: 1000, E: 1_600_000, Emin: 580_000 },
      no2: { Fb: 725, Ft: 475, Fv: 170, FcPerp: 625, Fc: 700, E: 1_300_000, Emin: 470_000 },
    },
  },
  easternWhitePine: {
    beamsAndStringers: {
      select: { Fb: 1050, Ft: 700, Fv: 125, FcPerp: 350, Fc: 675, E: 1_100_000, Emin: 400_000 },
      no1: { Fb: 875, Ft: 600, Fv: 125, FcPerp: 350, Fc: 575, E: 1_100_000, Emin: 400_000 },
      no2: { Fb: 575, Ft: 275, Fv: 125, FcPerp: 350, Fc: 400, E: 900_000, Emin: 330_000 },
    },
    postsAndTimbers: {
      select: { Fb: 975, Ft: 650, Fv: 125, FcPerp: 350, Fc: 725, E: 1_100_000, Emin: 400_000 },
      no1: { Fb: 800, Ft: 525, Fv: 125, FcPerp: 350, Fc: 625, E: 1_100_000, Emin: 400_000 },
      no2: { Fb: 450, Ft: 300, Fv: 125, FcPerp: 350, Fc: 325, E: 900_000, Emin: 330_000 },
    },
  },
  whiteOak: {
    beamsAndStringers: {
      select: { Fb: 1400, Ft: 825, Fv: 205, FcPerp: 800, Fc: 900, E: 1_000_000, Emin: 370_000 },
      no1: { Fb: 1200, Ft: 575, Fv: 205, FcPerp: 800, Fc: 775, E: 1_000_000, Emin: 370_000 },
      no2: { Fb: 750, Ft: 375, Fv: 205, FcPerp: 800, Fc: 475, E: 800_000, Emin: 290_000 },
    },
    postsAndTimbers: {
      select: { Fb: 1300, Ft: 875, Fv: 205, FcPerp: 800, Fc: 950, E: 1_000_000, Emin: 370_000 },
      no1: { Fb: 1050, Ft: 700, Fv: 205, FcPerp: 800, Fc: 825, E: 1_000_000, Emin: 370_000 },
      no2: { Fb: 600, Ft: 400, Fv: 205, FcPerp: 800, Fc: 400, E: 800_000, Emin: 290_000 },
    },
  },
};

/**
 * NDS Supplement Table 4A — Visually Graded Dimension Lumber (2″–4″ thick).
 * Used only for dressedS4S verification against AWC joist span tables.
 */
export const DIMENSION_DESIGN_VALUES: Record<TimberSpeciesId, Record<TimberGradeId, DesignValueRow>> = {
  douglasFir: {
    select: { Fb: 1500, Ft: 1000, Fv: 180, FcPerp: 625, Fc: 1700, E: 1_900_000, Emin: 690_000 },
    no1: { Fb: 1000, Ft: 675, Fv: 180, FcPerp: 625, Fc: 1500, E: 1_700_000, Emin: 620_000 },
    no2: { Fb: 900, Ft: 575, Fv: 180, FcPerp: 625, Fc: 1350, E: 1_600_000, Emin: 580_000 },
  },
  easternWhitePine: {
    select: { Fb: 1250, Ft: 575, Fv: 135, FcPerp: 350, Fc: 1200, E: 1_200_000, Emin: 440_000 },
    no1: { Fb: 775, Ft: 450, Fv: 135, FcPerp: 350, Fc: 1000, E: 1_200_000, Emin: 440_000 },
    no2: { Fb: 575, Ft: 275, Fv: 135, FcPerp: 350, Fc: 825, E: 1_100_000, Emin: 400_000 },
  },
  whiteOak: {
    select: { Fb: 1200, Ft: 700, Fv: 180, FcPerp: 800, Fc: 1100, E: 1_100_000, Emin: 400_000 },
    no1: { Fb: 1000, Ft: 575, Fv: 180, FcPerp: 800, Fc: 1000, E: 1_100_000, Emin: 400_000 },
    no2: { Fb: 850, Ft: 500, Fv: 180, FcPerp: 800, Fc: 825, E: 1_000_000, Emin: 370_000 },
  },
};

export const SPECIES_OPTIONS: { id: TimberSpeciesId; label: string; defaultMoisture: TimberMoisture }[] = [
  { id: 'easternWhitePine', label: 'Eastern White Pine', defaultMoisture: 'dry' },
  { id: 'douglasFir', label: 'Douglas Fir', defaultMoisture: 'dry' },
  { id: 'whiteOak', label: 'White Oak', defaultMoisture: 'green' },
];

export const GRADE_OPTIONS: { id: TimberGradeId; label: string }[] = [
  { id: 'no2', label: '#2' },
  { id: 'no1', label: '#1' },
  { id: 'select', label: 'Select' },
];

/** Map saved / legacy grade ids onto the current #2 / #1 / Select set. */
export function normalizeTimberGrade(raw: unknown): TimberGradeId {
  if (raw === 'no2') return 'no2';
  if (raw === 'select') return 'select';
  // Legacy #2/BTR used No.1 tabulated values.
  return 'no1';
}

/** Even-inch stock grid for rough timber estimating. */
export const STOCK_SECTIONS: { b: number; d: number; label: string }[] = [
  { b: 6, d: 6, label: '6×6' },
  { b: 6, d: 8, label: '6×8' },
  { b: 6, d: 10, label: '6×10' },
  { b: 6, d: 12, label: '6×12' },
  { b: 8, d: 8, label: '8×8' },
  { b: 8, d: 10, label: '8×10' },
  { b: 8, d: 12, label: '8×12' },
  { b: 8, d: 14, label: '8×14' },
  { b: 10, d: 10, label: '10×10' },
  { b: 10, d: 12, label: '10×12' },
  { b: 10, d: 14, label: '10×14' },
  { b: 12, d: 12, label: '12×12' },
  { b: 12, d: 14, label: '12×14' },
];

/** Dressed S4S sizes for common dimension lumber (verification only). */
export const DRESSED_DIMENSION_SIZES: Record<string, { b: number; d: number }> = {
  '2x6': { b: 1.5, d: 5.5 },
  '2x8': { b: 1.5, d: 7.25 },
  '2x10': { b: 1.5, d: 9.25 },
  '2x12': { b: 1.5, d: 11.25 },
};

export interface TimberSizingInputs {
  mode: TimberSizingMode;
  species: TimberSpeciesId;
  grade: TimberGradeId;
  moisture: TimberMoisture;
  /** Clear span (ft) for span→section mode. */
  clearSpanFt: number;
  /** Nominal section for section→span mode (and optional override). */
  nominalB: number;
  nominalD: number;
  loadMode: LoadInputMode;
  deadPsf: number;
  livePsf: number;
  tributaryWidthFt: number;
  /** Direct uniform load (plf) when loadMode === 'plf'. */
  loadPlf: number;
  /** Live portion of plf when loadMode === 'plf' (for deflection). */
  livePlf: number;
  deflectionLimit: DeflectionLimit;
  /** Load duration factor Cd (1.0 floor, 1.15 snow/roof live typical). */
  Cd: number;
  sizeBasis: TimberSizeBasis;
  /**
   * Repetitive member factor Cr. Production timber beams use 1.0.
   * Joist-table verification uses 1.15.
   */
  Cr: number;
  /**
   * When true, treat as dimension lumber (Table 4A values + dimension CF).
   * Verification flag only — UI always uses timber tables.
   */
  useDimensionLumberValues: boolean;
}

export interface CheckRatios {
  bending: number;
  shear: number;
  deflection: number;
}

export interface SectionCheckResult {
  passes: boolean;
  governing: GoverningCheck;
  ratios: CheckRatios;
  actualB: number;
  actualD: number;
  sizeClass: TimberSizeClass;
  FbPrime: number;
  FvPrime: number;
  EPrime: number;
  wTotalPlf: number;
  wLivePlf: number;
  fb: number;
  fv: number;
  deltaIn: number;
  deltaAllowIn: number;
  sectionModulus: number;
  inertia: number;
}

export interface SpanToSectionResult {
  ok: boolean;
  message: string;
  section: { b: number; d: number; label: string } | null;
  check: SectionCheckResult | null;
  candidatesTried: number;
}

export interface SectionToSpanResult {
  ok: boolean;
  message: string;
  maxClearSpanFt: number | null;
  governing: GoverningCheck;
  checkAtMax: SectionCheckResult | null;
}

function nonNeg(n: number): number {
  return Math.max(0, Number(n) || 0);
}

function round3(n: number): number {
  return Math.round(n * 1000) / 1000;
}

function round2(n: number): number {
  return Math.round(n * 100) / 100;
}

export function classifySize(nominalB: number, nominalD: number): TimberSizeClass {
  const thickness = Math.min(nominalB, nominalD);
  const width = Math.max(nominalB, nominalD);
  if (thickness < 5) return 'dimension';
  if (width > thickness + 2) return 'beamsAndStringers';
  return 'postsAndTimbers';
}

/**
 * Half-under: nominal − 0.5″ each face (production default).
 * dressedS4S: timber uses same −0.5″; dimension lumber uses published S4S (e.g. 2×10 → 1½×9¼).
 */
export function actualSection(
  nominalB: number,
  nominalD: number,
  sizeBasis: TimberSizeBasis,
): { b: number; d: number } {
  if (sizeBasis === 'dressedS4S' && Math.min(nominalB, nominalD) < 5) {
    const key = `${Math.round(nominalB)}x${Math.round(nominalD)}`;
    const dressed = DRESSED_DIMENSION_SIZES[key];
    if (dressed) return { b: dressed.b, d: dressed.d };
  }
  // Half-under (and timber S4S equivalent): −0.5″ each face
  return { b: Math.max(0.5, nominalB - 0.5), d: Math.max(0.5, nominalD - 0.5) };
}

/** Dimension-lumber size factor CF on Fb (NDS Table 4A). */
export function dimensionSizeFactorFb(nominalDepthIn: number): number {
  if (nominalDepthIn <= 4) return 1.5;
  if (nominalDepthIn <= 6) return 1.3;
  if (nominalDepthIn <= 8) return 1.2;
  if (nominalDepthIn <= 10) return 1.1;
  if (nominalDepthIn <= 12) return 1.0;
  return Math.pow(12 / nominalDepthIn, 1 / 9);
}

/** Beams & Stringers depth factor when d > 12″ (NDS 4.3.6). */
export function timberDepthFactorFb(actualDepthIn: number, sizeClass: TimberSizeClass): number {
  if (sizeClass === 'dimension') return 1;
  if (actualDepthIn <= 12) return 1;
  return Math.pow(12 / actualDepthIn, 1 / 9);
}

/**
 * Resolve reference design values.
 * #2 / #1 / Select map to No.2 / No.1 / Select Structural rows in Table 4D (or 4A).
 */
export function resolveDesignValues(
  species: TimberSpeciesId,
  grade: TimberGradeId,
  sizeClass: TimberSizeClass,
  useDimensionLumberValues: boolean,
): DesignValueRow {
  const gradeKey = normalizeTimberGrade(grade);
  if (useDimensionLumberValues || sizeClass === 'dimension') {
    return DIMENSION_DESIGN_VALUES[species][gradeKey];
  }
  const timberClass = sizeClass === 'beamsAndStringers' ? 'beamsAndStringers' : 'postsAndTimbers';
  return TIMBER_DESIGN_VALUES[species][timberClass][gradeKey];
}

/** NDS Table 4D / 4A wet-service factors (MC > 19%). */
export function wetServiceFactors(Fb: number, CF: number): {
  CmFb: number;
  CmFv: number;
  CmE: number;
} {
  const CmFb = Fb * CF <= 1150 ? 1.0 : 0.85;
  return { CmFb, CmFv: 0.97, CmE: 0.9 };
}

export function resolveLineLoads(inputs: TimberSizingInputs): { wTotalPlf: number; wLivePlf: number } {
  if (inputs.loadMode === 'plf') {
    return { wTotalPlf: nonNeg(inputs.loadPlf), wLivePlf: nonNeg(inputs.livePlf) };
  }
  const trib = nonNeg(inputs.tributaryWidthFt);
  const dead = nonNeg(inputs.deadPsf);
  const live = nonNeg(inputs.livePsf);
  return {
    wTotalPlf: (dead + live) * trib,
    wLivePlf: live * trib,
  };
}

export function sectionProperties(b: number, d: number): { A: number; S: number; I: number } {
  const A = b * d;
  const S = (b * d * d) / 6;
  const I = (b * d * d * d) / 12;
  return { A, S, I };
}

/**
 * Uniform-load midspan deflection (inches).
 * Δ = 5 w L⁴ / (384 E I) with w in plf, L in ft → Δ_in = 22.5 w L⁴ / (E I)
 */
export function deflectionInches(wPlf: number, spanFt: number, E: number, I: number): number {
  if (E <= 0 || I <= 0) return Infinity;
  return (22.5 * wPlf * Math.pow(spanFt, 4)) / (E * I);
}

export function checkSection(inputs: TimberSizingInputs, spanFt: number, nominalB: number, nominalD: number): SectionCheckResult {
  const sizeClass = classifySize(nominalB, nominalD);
  const { b, d } = actualSection(nominalB, nominalD, inputs.sizeBasis);
  const { A, S, I } = sectionProperties(b, d);
  const { wTotalPlf, wLivePlf } = resolveLineLoads(inputs);
  const base = resolveDesignValues(inputs.species, inputs.grade, sizeClass, inputs.useDimensionLumberValues);

  let CF = 1;
  if (inputs.useDimensionLumberValues || sizeClass === 'dimension') {
    CF = dimensionSizeFactorFb(nominalD);
  } else {
    CF = timberDepthFactorFb(d, sizeClass);
  }

  const wet = inputs.moisture === 'green' ? wetServiceFactors(base.Fb, CF) : { CmFb: 1, CmFv: 1, CmE: 1 };
  const Cd = nonNeg(inputs.Cd) || 1;
  const Cr = nonNeg(inputs.Cr) || 1;

  const FbPrime = base.Fb * Cd * CF * Cr * wet.CmFb;
  const FvPrime = base.Fv * Cd * wet.CmFv;
  const EPrime = base.E * wet.CmE;

  // Bending: M = w L² / 8 (ft-lb); fb = M * 12 / S
  const M_ftlb = (wTotalPlf * spanFt * spanFt) / 8;
  const fb = S > 0 ? (M_ftlb * 12) / S : Infinity;

  // Shear: V = w L / 2; fv = 1.5 V / A (rectangular)
  const V = (wTotalPlf * spanFt) / 2;
  const fv = A > 0 ? (1.5 * V) / A : Infinity;

  // Deflection on live load (AWC STJR / floor practice)
  const deltaIn = deflectionInches(wLivePlf, spanFt, EPrime, I);
  const deltaAllowIn = (spanFt * 12) / inputs.deflectionLimit;

  const ratios: CheckRatios = {
    bending: FbPrime > 0 ? fb / FbPrime : Infinity,
    shear: FvPrime > 0 ? fv / FvPrime : Infinity,
    deflection: deltaAllowIn > 0 ? deltaIn / deltaAllowIn : Infinity,
  };

  let governing: GoverningCheck = 'none';
  let maxRatio = 0;
  (['bending', 'shear', 'deflection'] as const).forEach((k) => {
    if (ratios[k] > maxRatio) {
      maxRatio = ratios[k];
      governing = k;
    }
  });

  const passes = ratios.bending <= 1.0005 && ratios.shear <= 1.0005 && ratios.deflection <= 1.0005;

  return {
    passes,
    governing: passes ? governing : governing,
    ratios,
    actualB: b,
    actualD: d,
    sizeClass,
    FbPrime: round2(FbPrime),
    FvPrime: round2(FvPrime),
    EPrime: Math.round(EPrime),
    wTotalPlf: round2(wTotalPlf),
    wLivePlf: round2(wLivePlf),
    fb: round2(fb),
    fv: round2(fv),
    deltaIn: round3(deltaIn),
    deltaAllowIn: round3(deltaAllowIn),
    sectionModulus: round3(S),
    inertia: round3(I),
  };
}

/** Sort stock by area then depth (smallest first). */
function sortedStock(): { b: number; d: number; label: string }[] {
  return STOCK_SECTIONS.slice().sort((a, b) => {
    const areaA = a.b * a.d;
    const areaB = b.b * b.d;
    if (areaA !== areaB) return areaA - areaB;
    if (a.d !== b.d) return a.d - b.d;
    return a.b - b.b;
  });
}

export function findMinSection(inputs: TimberSizingInputs): SpanToSectionResult {
  const span = nonNeg(inputs.clearSpanFt);
  if (span <= 0) {
    return { ok: false, message: 'Enter a clear span greater than zero.', section: null, check: null, candidatesTried: 0 };
  }
  const { wTotalPlf } = resolveLineLoads(inputs);
  if (wTotalPlf <= 0) {
    return { ok: false, message: 'Enter dead/live loads (or plf) greater than zero.', section: null, check: null, candidatesTried: 0 };
  }

  const stock = sortedStock();
  let tried = 0;
  for (let i = 0; i < stock.length; i++) {
    const s = stock[i];
    tried += 1;
    const check = checkSection(inputs, span, s.b, s.d);
    if (check.passes) {
      return {
        ok: true,
        message: `Smallest stock that passes: ${s.label} (analysis ${check.actualB}″ × ${check.actualD}″ half-under).`,
        section: s,
        check,
        candidatesTried: tried,
      };
    }
  }

  const last = stock[stock.length - 1];
  const lastCheck = checkSection(inputs, span, last.b, last.d);
  return {
    ok: false,
    message: `No stock section in the grid passes at ${span} ft. Largest tried: ${last.label}.`,
    section: null,
    check: lastCheck,
    candidatesTried: tried,
  };
}

/**
 * Binary search max clear span that still passes all checks.
 * Returns span rounded down to 0.01 ft (~⅛″).
 */
export function findMaxSpan(inputs: TimberSizingInputs): SectionToSpanResult {
  const { wTotalPlf } = resolveLineLoads(inputs);
  if (wTotalPlf <= 0) {
    return { ok: false, message: 'Enter loads greater than zero.', maxClearSpanFt: null, governing: 'none', checkAtMax: null };
  }
  if (inputs.nominalB <= 0 || inputs.nominalD <= 0) {
    return { ok: false, message: 'Enter a valid nominal section.', maxClearSpanFt: null, governing: 'none', checkAtMax: null };
  }

  let lo = 0.5;
  let hi = 80;
  // Ensure hi fails or expand
  for (let expand = 0; expand < 4; expand++) {
    const c = checkSection(inputs, hi, inputs.nominalB, inputs.nominalD);
    if (!c.passes) break;
    hi *= 1.5;
  }

  for (let i = 0; i < 48; i++) {
    const mid = (lo + hi) / 2;
    const c = checkSection(inputs, mid, inputs.nominalB, inputs.nominalD);
    if (c.passes) lo = mid;
    else hi = mid;
  }

  const maxSpan = Math.floor(lo * 100) / 100;
  const checkAtMax = checkSection(inputs, maxSpan, inputs.nominalB, inputs.nominalD);
  if (!checkAtMax.passes || maxSpan < 0.5) {
    return {
      ok: false,
      message: 'Section does not pass even at a short span under these loads.',
      maxClearSpanFt: null,
      governing: checkAtMax.governing,
      checkAtMax,
    };
  }

  return {
    ok: true,
    message: `Max clear span ≈ ${formatSpanFtIn(maxSpan)} (governed by ${checkAtMax.governing}).`,
    maxClearSpanFt: maxSpan,
    governing: checkAtMax.governing,
    checkAtMax,
  };
}

export function formatSpanFtIn(spanFt: number): string {
  if (!Number.isFinite(spanFt) || spanFt < 0) return '—';
  const feet = Math.floor(spanFt);
  const inches = Math.round((spanFt - feet) * 12);
  if (inches === 12) return `${feet + 1}'-0"`;
  return `${feet}'-${inches}"`;
}

export function parseSpanFtIn(text: string): number | null {
  const t = text.trim();
  const m = /^(\d+)\s*['′]\s*-?\s*(\d+(?:\.\d+)?)\s*["″]?$/.exec(t);
  if (m) return Number(m[1]) + Number(m[2]) / 12;
  const m2 = /^(\d+(?:\.\d+)?)\s*(?:ft|feet)?$/i.exec(t);
  if (m2) return Number(m2[1]);
  return null;
}

export function defaultInputs(): TimberSizingInputs {
  return {
    mode: 'spanToSection',
    species: 'douglasFir',
    grade: 'no1',
    moisture: 'dry',
    clearSpanFt: 12,
    nominalB: 8,
    nominalD: 10,
    loadMode: 'psfTrib',
    deadPsf: 10,
    livePsf: 40,
    tributaryWidthFt: 4,
    loadPlf: 200,
    livePlf: 160,
    deflectionLimit: 360,
    Cd: 1.0,
    sizeBasis: 'halfUnder',
    Cr: 1.0,
    useDimensionLumberValues: false,
  };
}

export function applyFloorPreset(inputs: TimberSizingInputs): TimberSizingInputs {
  return {
    ...inputs,
    deadPsf: 10,
    livePsf: 40,
    deflectionLimit: 360,
    Cd: 1.0,
  };
}

export function applyRoofPreset(inputs: TimberSizingInputs): TimberSizingInputs {
  return {
    ...inputs,
    deadPsf: 15,
    livePsf: 20,
    deflectionLimit: 240,
    Cd: 1.15,
  };
}
